import { api } from '../api/client.js';
import { createTabBar } from '../components/tab-bar.js';
import { createSkeleton } from '../components/skeleton.js';
import { getRouter } from '../router.js';

interface ListingsResponse {
  items: Array<{
    id: number;
    authorName: string;
    plotNumber: string | null;
    contactPhone: string | null;
    contactMaxUrl: string | null;
    title: string;
    body: string;
    submittedAt: string;
  }>;
  total: number;
}

export async function boardPage(): Promise<HTMLElement> {
  const router = getRouter();
  const page = document.createElement('div');
  page.className = 'page page--board';
  page.setAttribute('data-title', 'Объявления');

  // Header
  const header = document.createElement('header');
  header.className = 'page-header';
  header.innerHTML = `
    <svg class="page-header__logo" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <div class="page-header__text">
      <h1 class="page-header__title">СНТ "РОДНИК"</h1>
      <span class="page-header__subtitle">Садовое некоммерческое товарищество</span>
    </div>`;
  page.appendChild(header);

  // Tabs
  const featureListings = import.meta.env.VITE_FEATURE_LISTINGS === 'true';
  const tabs = createTabBar([
    { id: 'news', label: 'Новости', href: '/' },
    { id: 'contacts', label: 'Контакты', href: '/contacts' },
    { id: 'board', label: 'Объявления', href: '/board', hidden: !featureListings },
    { id: 'bus', label: 'Автобус', href: '/bus' },
  ], 'board', (href) => router.navigate(href));
  page.appendChild(tabs);

  // Content
  const content = document.createElement('div');
  content.className = 'page-content';

  const skeleton = createSkeleton('card', 3);
  content.appendChild(skeleton);
  page.appendChild(content);

  // Add listing button
  const addBtn = document.createElement('button');
  addBtn.className = 'add-listing-btn';
  addBtn.textContent = 'Подать объявление';
  content.appendChild(addBtn);

  const form = document.createElement('form');
  form.className = 'listing-form';
  form.style.display = 'none';
  // Honeypot
  const honeypot = document.createElement('input');
  honeypot.type = 'text';
  honeypot.name = 'website';
  honeypot.style.display = 'none';
  honeypot.tabIndex = -1;
  honeypot.autocomplete = 'off';
  form.appendChild(honeypot);

  form.innerHTML += `
    <div class="form-field">
      <label class="form-label">Ваше имя *</label>
      <input class="form-input" name="authorName" required minlength="2" maxlength="128">
    </div>
    <div class="form-field">
      <label class="form-label">Участок</label>
      <input class="form-input" name="plotNumber">
    </div>
    <div class="form-field">
      <label class="form-label">Телефон</label>
      <input class="form-input" name="contactPhone" type="tel">
    </div>
    <div class="form-field">
      <label class="form-label">Ссылка МАХ</label>
      <input class="form-input" name="contactMaxUrl" type="url">
    </div>
    <div class="form-field">
      <label class="form-label">Заголовок *</label>
      <input class="form-input" name="title" required minlength="5" maxlength="255">
    </div>
    <div class="form-field">
      <label class="form-label">Текст объявления *</label>
      <textarea class="form-input form-textarea" name="listingBody" required minlength="20" maxlength="5000"></textarea>
    </div>
    <button type="submit" class="btn btn--primary">Отправить</button>
  `;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const data = Object.fromEntries(fd);

    try {
      await api.post('/listings', data);
      form.reset();
      form.style.display = 'none';
      addBtn.style.display = 'block';
      alert('Объявление отправлено на модерацию');
    } catch (err) {
      console.error(err);
      alert('Ошибка при отправке');
    }
  });

  page.appendChild(form);

  addBtn.addEventListener('click', () => {
    addBtn.style.display = 'none';
    form.style.display = 'block';
  });

  // Fetch listings
  try {
    const data = await api.get<ListingsResponse>('/listings', { limit: 50, offset: 0 });
    // Replace skeleton with listings
    const existingSkeleton = content.querySelector('.skeleton-container');
    if (existingSkeleton) content.removeChild(existingSkeleton);

    if (data.items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty-state';
      empty.innerHTML = '<p>Объявлений пока нет</p>';
      content.appendChild(empty);
    } else {
      for (const listing of data.items) {
        const card = document.createElement('div');
        card.className = 'listing-card';
        card.innerHTML = `
          <h3 class="listing-card__title">${listing.title}</h3>
          <div class="listing-card__meta">
            <span>${listing.authorName}</span>
            ${listing.plotNumber ? `<span>уч. ${listing.plotNumber}</span>` : ''}
            <span>${new Date(listing.submittedAt).toLocaleDateString('ru-RU')}</span>
          </div>
          <div class="listing-card__body">${listing.body.split('\n').filter(Boolean).join('<br>')}</div>
          <div class="listing-card__actions">
            ${listing.contactPhone ? `<a href="tel:${listing.contactPhone}" class="btn btn--small">Позвонить</a>` : ''}
            ${listing.contactMaxUrl ? `<a href="${listing.contactMaxUrl}" target="_blank" class="btn btn--small">МАХ</a>` : ''}
          </div>
        `;
        content.appendChild(card);
      }
    }
  } catch (err) {
    console.error(err);
  }

  return page;
}
